/**
 * TimeHistoryExpander - Expandable Time field with history snapshots
 * Shows current time and allows navigation through chart state history
 */

import React, { useState, useCallback } from 'react';
import { HStack, Text, Badge, Divider, IconButton, Tooltip, Spinner } from '@chakra-ui/react';
import { CalendarIcon } from '@chakra-ui/icons';
import { ExpandableLabel } from '../ExpandableLabel';
import { useMultipleCharts } from '../../MultipleChartsContext';
import { ChartStateSnapshot, SNAPSHOT_SOURCES, SnapshotSource, CURRENT_LIVE_INDEX } from '../../../../../types/MultipleCharts';
import { ConfirmDeleteButton } from '../../../../common/ConfirmDeleteButton';
import { TimeMotionSettings } from './TimeMotionSettings';
import { formatDateFromISO, parseTimeWithMicroseconds } from '../../../../../config/dateTimeFormats';

interface TimeHistoryExpanderProps {
  chartId: string;
  datetimeIso: string;  // ISO 8601 string - single source of truth (includes timezone)
  disabled?: boolean;  // for share mode
}

/**
 * Get color scheme for snapshot source badge
 */
const getSourceColor = (source: SnapshotSource): string =>
  SNAPSHOT_SOURCES[source]?.color ?? 'gray';

/**
 * Individual snapshot row component
 */
const SnapshotRow: React.FC<{
  snapshot: ChartStateSnapshot;
  index: number;
  isActive: boolean;
  isOriginal: boolean;
  onClick: () => void;
  onDelete: () => void;
  disabled?: boolean;
}> = ({ snapshot, index, isActive, isOriginal, onClick, onDelete, disabled }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // Extract date, time, microseconds and timezone from chartDateTime (ISO 8601 format)
  const dt = snapshot.chartDateTime || '';
  const isIsoFormat = dt.includes('T');
  const formattedDate = isIsoFormat ? formatDateFromISO(dt) : '';
  const { time: formattedTime, microseconds, timezone: snapshotTimezone } = isIsoFormat
    ? parseTimeWithMicroseconds(dt)
    : { time: dt, microseconds: '', timezone: '' };

  // Can delete if: not original, not active, not disabled
  const canDelete = !isOriginal && !isActive && !disabled;

  // Show delete button when hovered OR when in confirmation mode
  const showDeleteButton = canDelete && (isHovered || isConfirming);

  return (
    <HStack
      spacing={2}
      px={2}
      py={1}
      bg={isActive ? 'blue.50' : 'transparent'}
      borderRadius="md"
      cursor={disabled ? 'default' : 'pointer'}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      _hover={disabled ? {} : { bg: isActive ? 'blue.100' : 'gray.50' }}
      transition="background 0.15s ease"
    >
      {/* Current indicator */}
      {isActive && (
        <Text fontSize="xs" color="blue.600" fontWeight="medium">
          current
        </Text>
      )}

      {/* Date and Time with microseconds and timezone */}
      <Text fontSize="sm" fontFamily="var(--font-ui-mono)" flex={1}>
        {formattedDate && <Text as="span">{formattedDate} </Text>}
        <Text as="span">{formattedTime}</Text>
        {microseconds && (
          <Text as="span" color="gray.400" fontSize="xs"> [{microseconds}]</Text>
        )}
        {snapshotTimezone && <Text as="span"> {snapshotTimezone}</Text>}
      </Text>

      {/* Source badge */}
      <Badge colorScheme={getSourceColor(snapshot.source)} fontSize="2xs" variant="outline">
        {snapshot.source}
      </Badge>

      {/* Delete button - show when hovered or confirming */}
      {showDeleteButton && (
        <ConfirmDeleteButton
          onDelete={onDelete}
          onConfirmStateChange={setIsConfirming}
          minW={5}
          h={5}
        />
      )}
    </HStack>
  );
};

/**
 * TimeHistoryExpander component
 * Expandable Time field that shows history snapshots when expanded
 */
export const TimeHistoryExpander: React.FC<TimeHistoryExpanderProps> = ({
  chartId,
  datetimeIso,
  disabled = false
}) => {
  const { getChartHistory, navigateHistory, getChart, deleteChartSnapshot, addCurrentTimeSnapshot } = useMultipleCharts();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAddingSnapshot, setIsAddingSnapshot] = useState(false);

  const history = getChartHistory(chartId);
  const chart = getChart(chartId);
  const currentIndex = chart?.currentHistoryIndex ?? CURRENT_LIVE_INDEX;

  // Handle snapshot click
  const handleSnapshotClick = useCallback((index: number) => {
    if (!disabled) {
      navigateHistory(chartId, index);
    }
  }, [chartId, disabled, navigateHistory]);

  // Handle snapshot delete
  const handleSnapshotDelete = useCallback((index: number) => {
    deleteChartSnapshot(chartId, index);
  }, [chartId, deleteChartSnapshot]);

  // Handle add current time snapshot
  const handleAddSnapshot = useCallback(async () => {
    if (!disabled && !isAddingSnapshot) {
      setIsAddingSnapshot(true);
      try {
        await addCurrentTimeSnapshot(chartId);
      } finally {
        setIsAddingSnapshot(false);
      }
    }
  }, [chartId, disabled, isAddingSnapshot, addCurrentTimeSnapshot]);

  // Toggle expand state
  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Get active snapshot info
  const activeIndex = currentIndex === CURRENT_LIVE_INDEX ? history.length - 1 : currentIndex;
  const activeSnapshot = history[activeIndex];
  const isNotOriginal = activeIndex !== 0 && activeSnapshot;

  // Parse time with microseconds and timezone from ISO string
  const { time: formattedCurrentTime, microseconds: currentMicroseconds, timezone } = parseTimeWithMicroseconds(datetimeIso);

  // Build time value string for display
  const timeValueDisplay = (
    <span className="planet-detail-value">
      {formattedCurrentTime}
      {currentMicroseconds && <span style={{ color: 'var(--chakra-colors-gray-400)', fontSize: '0.85em' }}> [{currentMicroseconds}]</span>}
      {timezone && <span> {timezone}</span>}
    </span>
  );

  // Add snapshot button component
  const snapshotButton = !disabled && (
    isAddingSnapshot ? (
      <Spinner size="xs" color="blue.500" thickness="2px" speed="0.65s" />
    ) : (
      <Tooltip label="Add current time snapshot" hasArrow>
        <IconButton
          aria-label="Add current time snapshot"
          icon={<CalendarIcon />}
          size="xs"
          variant="ghost"
          onClick={handleAddSnapshot}
          color="gray.500"
          minW={5}
          h={5}
          _hover={{ color: 'blue.600', bg: 'blue.50' }}
        />
      </Tooltip>
    )
  );

  // No history — just show plain time row, no expand icon
  if (history.length === 0) {
    return (
      <div className="planet-detail-row" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span className="planet-detail-label">Time:</span>
        {timeValueDisplay}
        {snapshotButton}
      </div>
    );
  }

  // Format label with time for ExpandableLabel (inline)
  const labelContent = (
    <span>
      Time: {formattedCurrentTime}
      {currentMicroseconds && <span style={{ color: 'var(--chakra-colors-gray-400)', fontSize: '0.85em' }}> [{currentMicroseconds}]</span>}
      {timezone && <span> {timezone}</span>}
    </span>
  );

  return (
    <div className="planet-detail-expandable-container">
      <div className="planet-detail-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ExpandableLabel
          label={labelContent}
          isExpanded={isExpanded}
          onToggle={handleToggle}
          ariaControls={`time-history-${chartId}`}
        />
        {isNotOriginal && (
          <Badge
            colorScheme={getSourceColor(activeSnapshot.source)}
            fontSize="2xs"
            variant="solid"
          >
            {activeSnapshot.source}
          </Badge>
        )}
        {snapshotButton}
      </div>

      {isExpanded && (
        <div
          id={`time-history-${chartId}`}
          className="planet-detail-subsection"
        >
          {/* Movement settings - only in non-share mode */}
          {!disabled && (
            <>
              <TimeMotionSettings chartId={chartId} disabled={disabled} />
              {history.length > 0 && <Divider my={2} borderColor="gray.200" />}
            </>
          )}

          {/* History snapshots */}
          {history.map((snapshot, index) => (
            <SnapshotRow
              key={snapshot.id}
              snapshot={snapshot}
              index={index}
              isActive={index === currentIndex || (currentIndex === CURRENT_LIVE_INDEX && index === history.length - 1)}
              isOriginal={index === 0}
              onClick={() => handleSnapshotClick(index)}
              onDelete={() => handleSnapshotDelete(index)}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TimeHistoryExpander;
